import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { apiPost } from "helpers/apiService";

const pickCreatedId = response => {
  const data =
    response?.data?.resultData ||
    response?.data?.data ||
    response?.data ||
    response ||
    null;

  if (!data) return null;
  if (typeof data === "number") return data;

  return (
    data.id ||
    data.projectMasterLandingPageTemplateId ||
    data.templateId ||
    null
  );
};

/**
 * Create Master Landing Page — collect name + description, then open the editor.
 */
const MasterLandingCreate = props => {
  document.title = "Create Master Landing Page | Realty Space";
  const navigate = useNavigate();
  const [templateTitle, setTemplateTitle] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async event => {
    event.preventDefault();
    const title = templateTitle.trim();

    if (!title) {
      toast.error("Please enter a template name.");
      return;
    }

    const payload = {
      templateTitle: title,
      templateDescription: templateDescription.trim(),
      templateContent: "",
      templateCSS: "",
      templateType: 1,
      publishStatusId: 0,
      isActive: true,
    };

    try {
      setSaving(true);
      toast.loading("Creating template...");

      const response = await apiPost(
        "ProjectMasterLandingPageTemplate",
        payload
      );

      toast.dismiss();
      toast.success("Template created");

      const createdId = pickCreatedId(response);
      if (createdId) {
        navigate(`/master-landing-page/edit/${createdId}`);
      } else {
        navigate("/master-landing-page");
      }
    } catch (error) {
      toast.dismiss();
      toast.error(
        error?.response?.data?.validationErrors?.[0] ||
          error?.response?.data?.message ||
          "Failed to create template"
      );
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      <ToastContainer autoClose={2000} />
      <div className="page-content project-page-content">
        <Container fluid>
          <Row className="project-header mb20">
            <Col sm="12">
              <Breadcrumbs
                title={props.t("Master")}
                breadcrumbItem={props.t("Create Master Landing Page")}
              />
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col lg="8" xl="6">
              <Card>
                <CardBody>
                  <h4 className="mb-1">New Master Landing Page</h4>
                  <p className="text-muted mb-4">
                    Enter the template name and description, then continue to
                    the page designer.
                  </p>

                  <Form onSubmit={handleSubmit}>
                    <FormGroup>
                      <Label htmlFor="templateTitle">
                        Template Name <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        id="templateTitle"
                        name="templateTitle"
                        value={templateTitle}
                        onChange={e => setTemplateTitle(e.target.value)}
                        placeholder="e.g. Altaire Penthouse"
                        required
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="templateDescription">Description</Label>
                      <Input
                        type="textarea"
                        id="templateDescription"
                        name="templateDescription"
                        value={templateDescription}
                        onChange={e => setTemplateDescription(e.target.value)}
                        placeholder="Optional short description"
                        rows="3"
                      />
                    </FormGroup>

                    <div className="d-flex gap-2">
                      <Button color="primary" type="submit" disabled={saving}>
                        {saving ? "Creating..." : "Create & Design"}
                      </Button>
                      <Link
                        to="/master-landing-page"
                        className="btn btn-secondary"
                      >
                        Cancel
                      </Link>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

MasterLandingCreate.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(MasterLandingCreate);
